import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Bomb, Users, Building2, Plus, Trash2, AlertTriangle, RotateCcw, Download,
} from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import CompanyDeleteDialog from "./CompanyDeleteDialog";
import { formatDateTime } from "@/lib/formatDate";

function fmtDate(iso) {
  return formatDateTime(iso) || "—";
}

export default function SystemAdminTab({ me }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create user state
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", is_super_admin: false });
  const [creatingUser, setCreatingUser] = useState(false);

  // Create company state
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: "", owner_email: "", currency: "CHF" });
  const [creatingCompany, setCreatingCompany] = useState(false);

  // Full Reset Modal
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetting, setResetting] = useState(false);

  // Company Delete Dialog
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, c] = await Promise.all([
        apiClient.get("/admin/system/stats"),
        apiClient.get("/admin/users"),
        apiClient.get("/admin/companies"),
      ]);
      setStats(s.data);
      setUsers(u.data || []);
      setCompanies(c.data || []);
    } catch (e) {
      toast.error("System-Verwaltung konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ---------- Create user ----------
  const createUser = async () => {
    if (!newUser.email || !newUser.password || newUser.password.length < 8) {
      toast.error("E-Mail und Passwort (mind. 8 Zeichen) erforderlich");
      return;
    }
    setCreatingUser(true);
    try {
      await apiClient.post("/admin/users", newUser);
      toast.success(`User ${newUser.email} angelegt`);
      setNewUser({ email: "", password: "", is_super_admin: false });
      setShowCreateUser(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setCreatingUser(false);
    }
  };

  // ---------- Delete user ----------
  const deleteUser = async (user) => {
    // Iter121: Verschärfte Confirmation bei Super-Admin
    const msg = user.is_super_admin
      ? `⚠️ ${user.email} ist SUPER-ADMIN.\n\nWenn das der letzte Super-Admin ist, wird der Löschvorgang serverseitig blockiert.\n\nWirklich hart löschen?`
      : `User ${user.email} wirklich hart löschen?`;
    if (!window.confirm(msg)) return;
    try {
      await apiClient.delete(`/admin/users/${user.id}`);
      toast.success("User gelöscht");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  // ---------- Create company ----------
  const createCompany = async () => {
    if (!newCompany.name || !newCompany.owner_email) {
      toast.error("Name und Owner-E-Mail erforderlich");
      return;
    }
    setCreatingCompany(true);
    try {
      await apiClient.post("/admin/companies", newCompany);
      toast.success(`Firma "${newCompany.name}" angelegt`);
      setNewCompany({ name: "", owner_email: "", currency: "CHF" });
      setShowCreateCompany(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setCreatingCompany(false);
    }
  };

  // ---------- Full Reset ----------
  const fullReset = async () => {
    if (resetText !== "ALLES LÖSCHEN") {
      toast.error('Bitte exakt "ALLES LÖSCHEN" eintippen');
      return;
    }
    setResetting(true);
    try {
      const r = await apiClient.post("/admin/system/full-reset", {
        confirmation_text: resetText,
      });
      const total = Object.values(r.data.deleted || {}).reduce(
        (a, b) => a + (typeof b === "number" && b > 0 ? b : 0), 0
      );
      toast.success(`System zurückgesetzt — ${total} Datensätze gelöscht`);
      setShowResetModal(false);
      setResetText("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset fehlgeschlagen");
    } finally {
      setResetting(false);
    }
  };

  // ---------- Download archive ----------
  const downloadArchive = async (company) => {
    try {
      const r = await apiClient.post(
        `/admin/companies/${company.id}/export-archive`,
        {},
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m?.[1] || `archiv_${company.name}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Archiv heruntergeladen");
    } catch (e) {
      toast.error("Download fehlgeschlagen");
    }
  };

  const showResetButton = stats && !stats.is_clean;

  return (
    <div className="space-y-6" data-testid="system-admin-tab">

      {/* Card 1: Full Reset (versteckt, wenn System leer) */}
      {showResetButton && (
        <Card className="p-5 border-2 border-rose-300 bg-gradient-to-br from-rose-50 to-rose-100/30 shadow-sm" data-testid="full-reset-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="h-11 w-11 rounded-lg bg-rose-600 text-white flex items-center justify-center shrink-0">
                <Bomb className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-rose-900">System-Reset (Danger Zone)</h2>
                <p className="text-sm text-rose-800 mt-0.5">
                  Löscht <b>alle</b> Firmen, User (außer dir), Belege, Rechnungen, Journale, Lohnläufe,
                  Anlagen und hochgeladenen Dateien. Du bleibst als Super-Admin angemeldet.
                </p>
                {stats && (
                  <p className="text-xs text-rose-700 mt-1.5 font-medium">
                    Aktuell im System: {stats.users_excluding_self} User · {stats.companies} Firmen ·{" "}
                    {stats.receipts} Belege · {stats.invoices + stats.bills} Rechnungen ·{" "}
                    {stats.journal_entries} Journal-Einträge
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="destructive"
              onClick={() => setShowResetModal(true)}
              data-testid="full-reset-trigger"
            >
              <Bomb className="h-4 w-4 mr-1.5" />
              Alles löschen
            </Button>
          </div>
        </Card>
      )}

      {!showResetButton && stats?.is_clean && (
        <Card className="p-4 border border-emerald-200 bg-emerald-50/40" data-testid="system-clean-card">
          <p className="text-sm text-emerald-800">
            ✓ System ist bereits sauber — keine Firmen, User oder Daten vorhanden.
          </p>
        </Card>
      )}

      {/* Card 2: User Management — Iter122 in Plattform-Admins vs. Kunden gesplittet */}
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="user-mgmt-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">
              User-Verwaltung ({users.length})
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={refresh} data-testid="user-mgmt-refresh">
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={() => setShowCreateUser(true)} data-testid="user-mgmt-create">
              <Plus className="h-4 w-4 mr-1" />
              Neuer User
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Lädt …</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-slate-500">Keine User vorhanden.</p>
        ) : (
          (() => {
            const admins = users.filter((u) => u.is_super_admin);
            const customers = users.filter((u) => !u.is_super_admin);
            const renderTable = (list, testid) => (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={testid}>
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                      <th className="py-2 pr-4">E-Mail</th>
                      <th className="py-2 pr-4">Rolle</th>
                      <th className="py-2 pr-4">Firmen</th>
                      <th className="py-2 pr-4">Erstellt</th>
                      <th className="py-2 pr-4 text-right">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((u) => {
                      const isMe = u.id === me?.id;
                      return (
                        <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-mgmt-row-${u.id}`}>
                          <td className="py-2 pr-4 font-medium text-slate-900">
                            {u.email}
                            {isMe && <span className="ml-1.5 text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200">du</span>}
                          </td>
                          <td className="py-2 pr-4">
                            {u.is_super_admin ? (
                              <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">Super-Admin</span>
                            ) : (
                              <span className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 rounded">Kunde</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-slate-600">{u.company_count}</td>
                          <td className="py-2 pr-4 text-slate-500 text-xs">{fmtDate(u.created_at)}</td>
                          <td className="py-2 pr-4 text-right">
                            {!isMe && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteUser(u)}
                                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                                data-testid={`user-mgmt-delete-${u.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                                Löschen
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
            return (
              <div className="space-y-5">
                <div>
                  <h3 className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-2 flex items-center gap-2">
                    🛡️ Plattform-Admins ({admins.length})
                    <span className="text-[10px] text-slate-500 normal-case font-normal">verwalten Abos, Keys & Userdaten — besitzen keine eigenen Firmen</span>
                  </h3>
                  {admins.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">Keine Plattform-Admins.</p>
                  ) : (
                    renderTable(admins, "user-mgmt-admin-table")
                  )}
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-2">
                    👤 Kunden ({customers.length})
                    <span className="text-[10px] text-slate-500 normal-case font-normal">besitzen Firmen und buchen ihre Buchhaltung</span>
                  </h3>
                  {customers.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">Noch keine Kunden registriert.</p>
                  ) : (
                    renderTable(customers, "user-mgmt-customer-table")
                  )}
                </div>
              </div>
            );
          })()
        )}
      </Card>

      {/* Card 3: Company Management */}
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="company-mgmt-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">
              Firmen-Verwaltung ({companies.length})
            </h2>
          </div>
          <Button size="sm" onClick={() => setShowCreateCompany(true)} data-testid="company-mgmt-create">
            <Plus className="h-4 w-4 mr-1" />
            Neue Firma
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Lädt …</p>
        ) : companies.length === 0 ? (
          <p className="text-sm text-slate-500">Keine Firmen vorhanden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="company-mgmt-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <th className="py-2 pr-4">Firma</th>
                  <th className="py-2 pr-4">Owner</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Erstellt</th>
                  <th className="py-2 pr-4 text-right">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`company-mgmt-row-${c.id}`}>
                    <td className="py-2 pr-4 font-medium text-slate-900">{c.name}</td>
                    <td className="py-2 pr-4 text-slate-600">{c.owner_email || "—"}</td>
                    <td className="py-2 pr-4 text-slate-600">{c.user_count}</td>
                    <td className="py-2 pr-4 text-slate-500 text-xs">{fmtDate(c.created_at)}</td>
                    <td className="py-2 pr-4 text-right space-x-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadArchive(c)}
                        data-testid={`company-mgmt-archive-${c.id}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1" />
                        Archiv
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteTarget(c)}
                        className="text-rose-700 border-rose-200 hover:bg-rose-50"
                        data-testid={`company-mgmt-delete-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Löschen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---------- Modals ---------- */}

      {/* Create User Modal */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Neuen User anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="nu-email">E-Mail</Label>
              <Input
                id="nu-email" type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="kunde@example.ch"
                data-testid="create-user-email"
              />
            </div>
            <div>
              <Label htmlFor="nu-pw">Passwort (min. 8 Zeichen)</Label>
              <Input
                id="nu-pw" type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Mind. 8 Zeichen"
                data-testid="create-user-password"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={newUser.is_super_admin}
                onCheckedChange={(v) => setNewUser({ ...newUser, is_super_admin: !!v })}
                data-testid="create-user-superadmin"
              />
              <span className="text-sm text-slate-700">Als Super-Admin anlegen</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Abbrechen</Button>
            <Button onClick={createUser} disabled={creatingUser} data-testid="create-user-submit">
              {creatingUser ? "Lege an…" : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Company Modal */}
      <Dialog open={showCreateCompany} onOpenChange={setShowCreateCompany}>
        <DialogContent data-testid="create-company-dialog">
          <DialogHeader>
            <DialogTitle>Neue Firma anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="nc-name">Firmenname</Label>
              <Input
                id="nc-name"
                value={newCompany.name}
                onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                placeholder="Muster GmbH"
                data-testid="create-company-name"
              />
            </div>
            <div>
              <Label htmlFor="nc-owner">Owner E-Mail (existierender User)</Label>
              <Input
                id="nc-owner" type="email"
                value={newCompany.owner_email}
                onChange={(e) => setNewCompany({ ...newCompany, owner_email: e.target.value })}
                placeholder="owner@example.ch"
                data-testid="create-company-owner"
              />
            </div>
            <div>
              <Label htmlFor="nc-curr">Währung</Label>
              <Input
                id="nc-curr"
                value={newCompany.currency}
                onChange={(e) => setNewCompany({ ...newCompany, currency: e.target.value })}
                placeholder="CHF"
                data-testid="create-company-currency"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCompany(false)}>Abbrechen</Button>
            <Button onClick={createCompany} disabled={creatingCompany} data-testid="create-company-submit">
              {creatingCompany ? "Lege an…" : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Reset Modal */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent data-testid="full-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" />
              System komplett zurücksetzen?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700">
              Diese Aktion löscht <b>alle</b> Daten im System (außer deinem Super-Admin-Account und den
              globalen System-Konfigurationen wie KI-Keys und Allowlist).
            </p>
            {stats && (
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs space-y-0.5">
                <p>• {stats.users_excluding_self} weitere User</p>
                <p>• {stats.companies} Firmen</p>
                <p>• {stats.receipts} Belege</p>
                <p>• {stats.invoices} Ausgangsrechnungen · {stats.bills} Eingangsrechnungen</p>
                <p>• {stats.journal_entries} Journal-Einträge</p>
                <p className="font-semibold mt-1.5 text-rose-700">
                  + alle hochgeladenen Dateien (Belege-PDFs)
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="reset-text">
                Tippe zur Bestätigung: <span className="font-mono bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">ALLES LÖSCHEN</span>
              </Label>
              <Input
                id="reset-text"
                value={resetText}
                onChange={(e) => setResetText(e.target.value)}
                className="mt-2 font-mono"
                placeholder="ALLES LÖSCHEN"
                data-testid="full-reset-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowResetModal(false); setResetText(""); }} data-testid="full-reset-cancel">
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={fullReset}
              disabled={resetText !== "ALLES LÖSCHEN" || resetting}
              data-testid="full-reset-confirm"
            >
              <Bomb className="h-4 w-4 mr-1.5" />
              {resetting ? "Lösche…" : "Jetzt alles löschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Delete Dialog */}
      <CompanyDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        company={deleteTarget}
        onDeleted={refresh}
      />
    </div>
  );
}
